import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';
import { applicable, every, itemKind, loincCode, medicationAdded, patient, taskCategory } from './builders';
import { aceInhibitorsValueSet, angiotensinReceptorBlockersValueSet } from '../valueSets/rxNormDrugClasses';

export const HYPERTENSION_MONITORING_URL = 'https://liza.health/fhir/PlanDefinition/hypertension-monitoring';
export const HYPERTENSION_MONITORING_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const HYPERTENSION_MONITORING_CANONICAL = `${HYPERTENSION_MONITORING_URL}|${HYPERTENSION_MONITORING_VERSION}`;

const AHA_ACC_HTN_2025 = {
  title: 'AHA/ACC Guideline for the Prevention, Detection, Evaluation and Management of High Blood Pressure (2025)',
  url: 'https://www.ahajournals.org/hypertension-guidelines',
} as const;

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const HYPERTENSION_CODES = {
  BLOOD_PRESSURE: LOINC_CODES.BLOOD_PRESSURE_PANEL,
  CREATININE: LOINC_CODES.CREATININE,
  EGFR: LOINC_CODES.EGFR,
  POTASSIUM: LOINC_CODES.POTASSIUM,
} as const;

/**
 * The variables this protocol binds before evaluating any condition.
 *
 * `%onRasi` is one variable covering two ValueSets. Guidelines say "RASi"; ATC splits it into ACE
 * inhibitors (C09A) and ARBs (C09C), so the engine reads both and binds the union — the distinction
 * matters to ATC, not to the monitoring the guideline asks for.
 */
export const HYPERTENSION_CONTEXT_VARIABLES = [
  {
    name: '%onRasi',
    description: 'Boolean: an active MedicationRequest whose ingredient RxCUI is in the ACE inhibitor or ARB ValueSet.',
  },
  {
    name: '%rasiStartedAt',
    description: 'dateTime: authoredOn of the earliest active RASi MedicationRequest; empty when not on one.',
  },
  {
    name: '%potassium',
    description: `Latest Observation with LOINC ${LOINC_CODES.POTASSIUM}, from any source; empty when none.`,
  },
  {
    name: '%creatinine',
    description: `Latest Observation with LOINC ${LOINC_CODES.CREATININE}, from any source; empty when none.`,
  },
] as const;

export type HypertensionContextVariable = (typeof HYPERTENSION_CONTEXT_VARIABLES)[number]['name'];

/**
 * `I10` is essential hypertension; `I11`-`I13` are the hypertensive heart and kidney disease
 * categories, all matched as ICD-10 prefixes. The SNOMED code is matched exactly.
 */
export const HYPERTENSION_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'I10', display: 'Essential (primary) hypertension' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'I11', display: 'Hypertensive heart disease' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'I12', display: 'Hypertensive chronic kidney disease' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'I13', display: 'Hypertensive heart and chronic kidney disease' },
  { system: CODE_SYSTEMS.SNOMED, code: '38341003', display: 'Hypertensive disorder' },
] as const;

const citation = (locus: string): PlanDefinitionAction['documentation'] => [
  { type: 'citation', display: `${AHA_ACC_HTN_2025.title}, ${locus}`, url: AHA_ACC_HTN_2025.url },
];

// --- actions ------------------------------------------------------------------------------------------

/**
 * No cadence. The guideline recommends home monitoring for anyone on treatment but names no interval,
 * and the only cadence it does give — monthly until control is achieved — is conditional on a state
 * this protocol cannot read: whether the patient's pressure is at goal is a clinical judgement, not a
 * threshold the engine can apply to a single reading.
 *
 * As-needed puts the measurement on the plan without asserting a schedule the guideline declines to
 * give, and without implying the patient is uncontrolled.
 *
 * Unconditional, despite the guideline scoping it to treated patients. Gating it would need a general
 * "on any antihypertensive" signal, and the only drug variable here is `%onRasi` — one class among
 * several. Untreated hypertension is also the case where home readings matter most, since they are
 * what a treatment decision is made on, so firing for everyone errs in the useful direction.
 */
const homeBloodPressure = (): PlanDefinitionAction => ({
  id: 'home-blood-pressure',
  title: 'Home blood pressure',
  description: 'Home blood pressure, as needed.',
  code: loincCode(HYPERTENSION_CODES.BLOOD_PRESSURE),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  documentation: citation('Section 3.1.4 Recommendation 2'),
});

/**
 * The annual labs: electrolytes for the adverse effects of therapy, and creatinine with eGFR for
 * kidney disease developing or progressing.
 *
 * Unconditional, for the same reason as home blood pressure above — there is no general
 * "on antihypertensive therapy" variable to gate on, only `%onRasi`. The guideline frames these
 * around treatment, so an untreated patient gets labs the guideline does not strictly ask for; that
 * is the safe direction, since kidney disease is what the labs are looking for and hypertension is
 * why it is worth looking. Worth revisiting once more antihypertensive classes are curated.
 *
 * The interval is the guideline's "at least annually" floor. Its recommendation number could not be
 * verified against primary text — the publisher refuses automated access — so the citation names the
 * section rather than asserting a number that may not exist.
 */
const annualLab = (id: string, title: string, description: string, code: string): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  timingTiming: every(1, 'a'),
  documentation: citation('laboratory monitoring, annual'),
});

const potassiumAnnual = annualLab(
  'potassium-annual',
  'Potassium',
  'Potassium at least yearly, for the adverse effects of therapy.',
  HYPERTENSION_CODES.POTASSIUM,
);

const creatinineAnnual = annualLab(
  'creatinine-annual',
  'Creatinine',
  'Creatinine at least yearly, for kidney disease developing or progressing.',
  HYPERTENSION_CODES.CREATININE,
);

const egfrAnnual = annualLab(
  'egfr-annual',
  'eGFR',
  'eGFR at least yearly, for kidney disease developing or progressing.',
  HYPERTENSION_CODES.EGFR,
);

/**
 * The post-initiation check after starting a RASi, due at the guideline's shorter bound of two weeks.
 *
 * Three pieces make this fire once rather than recur, and all three are load-bearing:
 *
 * - `timingDuration` rather than `timingTiming`. `anchorOf` in the planner only consults an action's
 *   trigger when it carries a duration; an action with a recurring timing anchors on its own latest
 *   observation instead, which would measure the window from the last potassium draw rather than
 *   from the prescription.
 * - `trigger` naming the RASi ValueSets, which is what the anchor resolves to the earliest active
 *   prescription's `authoredOn`.
 * - A condition that goes **false** once a result lands after that date. Without it the action would
 *   keep applying for as long as the patient stays on the drug.
 *
 * This is the shape `lipidsAfterStatin` uses in the diabetes protocol, for the same reason.
 */
const afterRasi = (
  id: string,
  title: string,
  description: string,
  code: string,
  resultVariable: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  trigger: medicationAdded([aceInhibitorsValueSet.url!, angiotensinReceptorBlockersValueSet.url!]),
  condition: applicable(
    `%rasiStartedAt.exists() and (${resultVariable}.empty() or ${resultVariable}.effective < %rasiStartedAt)`,
    'On an ACE inhibitor or ARB with no result since it was started',
  ),
  timingDuration: { value: 2, unit: 'weeks', system: CODE_SYSTEMS.UCUM, code: 'wk' },
  documentation: citation('RASi initiation monitoring'),
});

const potassiumAfterRasi = afterRasi(
  'potassium-after-rasi',
  'Potassium after starting a RASi',
  'Potassium 2-4 weeks after starting an ACE inhibitor or ARB; due at 2 weeks.',
  HYPERTENSION_CODES.POTASSIUM,
  '%potassium',
);

const creatinineAfterRasi = afterRasi(
  'creatinine-after-rasi',
  'Creatinine after starting a RASi',
  'Creatinine 2-4 weeks after starting an ACE inhibitor or ARB; due at 2 weeks.',
  HYPERTENSION_CODES.CREATININE,
  '%creatinine',
);

// --- the protocol -------------------------------------------------------------------------------------

/**
 * What the 2025 AHA/ACC guideline asks for on an ongoing basis in treated hypertension.
 *
 * Two things the issue body lists that are not here. **Heart rate** has no monitoring cadence in this
 * guideline, or in the heart failure, chronic coronary disease or atrial fibrillation guidelines —
 * only rate *targets*, which are a different thing. And **office blood pressure once controlled** has
 * no interval either: the guideline gives monthly-until-control, which is conditional on a clinical
 * judgement the engine cannot make from a single reading.
 *
 * The post-initiation RASi checks are bounded rather than recurring. Their condition requires the
 * prescription to exist, so they stop applying once the patient is off the drug; the engine's
 * more-frequent-wins dedup is what keeps them from competing with the annual labs on the same codes.
 *
 * A citation caveat worth carrying: the 2025 guideline's recommendation numbers could not be verified
 * against primary text, because the publisher refuses automated access. Every citation here names the
 * section it sits in rather than asserting a number. The intervals themselves are corroborated.
 */
export const hypertensionMonitoring: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: HYPERTENSION_MONITORING_URL,
  version: HYPERTENSION_MONITORING_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: HYPERTENSION_MONITORING_URL }],
  name: 'hypertension-monitoring',
  title: 'Hypertension — ongoing monitoring',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-16',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in hypertension, expanded onto a care-plan topic when a hypertension Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...HYPERTENSION_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: AHA_ACC_HTN_2025.title, url: AHA_ACC_HTN_2025.url }],
  action: [
    {
      id: 'ongoing-monitoring',
      title: 'Ongoing monitoring',
      description: 'Results tracked on an ongoing basis for hypertension.',
      action: [
        homeBloodPressure(),
        potassiumAnnual,
        creatinineAnnual,
        egfrAnnual,
        potassiumAfterRasi,
        creatinineAfterRasi,
      ],
    },
  ],
};
